import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';

import { PATH_COST_DEFAULT } from '../../app/constants/paths.constant';
import type { ReferencesContext_GenericStatus } from '../../contexts/ReferencesContext';

import { STATUS_ID } from './constants/data-grid.constant';
import type { CostListCmp_Cost } from './hooks/useFindAllCostsQuery.hook';

interface Props {
  readonly cost: CostListCmp_Cost;
}

const Action = ({ cost }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_COST_DEFAULT, { id: cost._id });
    void navigate(path);
  }, [navigate, cost._id]);

  const actionLabel = 'Consulter le Coût';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<CostListCmp_Cost>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (params: Readonly<GridRowParams<CostListCmp_Cost>>) => [
      <Action cost={params.row} key="consult" />,
    ],
  },
  {
    field: nameof<CostListCmp_Cost>('code'),
    headerName: 'Code',
    width: 350,
    description: 'Code',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<CostListCmp_Cost>('label'),
    headerName: 'Libellé',
    width: 400,
    description: 'Libellé',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: STATUS_ID,
    headerName: 'Statut',
    width: 200,
    description: 'Statut',
    editable: false,
    sortable: true,
    hideable: true,
    type: 'singleSelect',
    valueGetter: (_: unknown, row: CostListCmp_Cost) =>
      genericStatuses.find(
        (genericStatus) => genericStatus._id === row.statusId,
      )?.code ?? 'Inconnu',
    valueOptions: genericStatuses.map((genericStatus) => ({
      value: genericStatus.code,
      label: genericStatus.label,
    })),
  },
];
